import React, { useState } from "react";
import {
  FaBuilding,
  FaChartLine,
  FaHeadset,
  FaCreditCard,
  FaUserTie,
  FaCalendarCheck,
  FaFileInvoiceDollar,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaBell,
  FaEnvelope,
} from "react-icons/fa";
import { toast } from "react-toastify";
import generalApi from "../api/generalApi";

const CorporateBookings = () => {
  const [email, setEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const result = await generalApi.joinCorporateWaitlist(email);
      if (result.success) {
        toast.success(result.message || `Thank you! We'll notify you at ${email} when we launch.`);
        setEmail("");
      } else {
        toast.error(result.message || "Failed to join waitlist. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: FaBuilding,
      title: "Centralized Management",
      description:
        "Book and manage all your corporate stays from a single, intuitive dashboard",
    },
    {
      icon: FaChartLine,
      title: "Expense Tracking",
      description:
        "Monitor and analyze travel expenses with detailed reporting and analytics",
    },
    {
      icon: FaHeadset,
      title: "Dedicated Support",
      description:
        "Access our specialized corporate support team for priority assistance 24/7",
    },
    {
      icon: FaCreditCard,
      title: "Flexible Billing",
      description:
        "Simplified invoicing and billing options tailored to your business needs",
    },
    {
      icon: FaUserTie,
      title: "Account Manager",
      description:
        "Get a dedicated account manager to handle all your corporate travel needs",
    },
    {
      icon: FaCalendarCheck,
      title: "Bulk Booking",
      description:
        "Book multiple rooms across different locations with just a few clicks",
    },
    {
      icon: FaFileInvoiceDollar,
      title: "Custom Reports",
      description:
        "Generate comprehensive travel reports and expense summaries instantly",
    },
    {
      icon: FaShieldAlt,
      title: "Secure & Compliant",
      description:
        "Enterprise-grade security with full compliance to corporate policies",
    },
  ];

  const benefits = [
    "Save up to 30% on corporate travel costs",
    "Streamline booking process for your team",
    "Real-time booking confirmations",
    "Centralized billing and invoicing",
    "Priority customer support",
    "Customizable travel policies",
  ];

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Hero Section */}
      <header className="relative h-[320px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c"
          alt="Corporate Bookings"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(238, 46, 36, 0.85) 0%, rgba(255, 196, 0, 0.75) 100%)",
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(255, 255, 255, 0.2)",
              padding: "6px 16px",
              borderRadius: "20px",
              marginBottom: "16px",
              fontSize: "0.85rem",
              fontWeight: "600",
            }}
          >
            <FaBuilding style={{ fontSize: "0.9rem" }} />
            Enterprise Solution
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold mb-3 text-center"
            style={{
              letterSpacing: "-0.8px",
              textShadow: "0 2px 20px rgba(0,0,0,0.3)",
              fontWeight: "800",
            }}
          >
            Corporate Bookings Portal
          </h1>
          <p
            className="text-base md:text-lg text-center max-w-2xl mb-4"
            style={{
              opacity: 0.95,
              fontWeight: "500",
              textShadow: "0 1px 10px rgba(0,0,0,0.2)",
            }}
          >
            Streamline your business travel with our enterprise-grade booking
            platform
          </p>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: "#FFC400",
              color: "#2E2E2E",
              fontSize: "0.9rem",
              fontWeight: "700",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            <FaClock />
            Coming Soon
          </div>
        </div>
      </header>

      {/* Introduction Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{
              color: "#EE2E24",
              letterSpacing: "-0.8px",
              fontWeight: "800",
            }}
          >
            Revolutionize Your Corporate Travel
          </h2>
          <p
            style={{
              color: "#64748B",
              fontSize: "1rem",
              lineHeight: "1.7",
              marginBottom: "2rem",
            }}
          >
            Our upcoming corporate bookings portal is designed to streamline
            your business travel needs. Get dedicated tools to manage
            reservations, track expenses, and ensure your team's travel is
            organized, efficient, and cost-effective.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section
        className="py-12"
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <h2
            className="text-2xl md:text-3xl font-bold mb-8 text-center"
            style={{
              color: "#EE2E24",
              letterSpacing: "-0.8px",
              fontWeight: "800",
            }}
          >
            Powerful Features for Modern Businesses
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg p-5 transform transition-all duration-300"
                  style={{
                    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 30px rgba(238, 46, 36, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(0,0,0,0.08)";
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded-full mb-3 mx-auto"
                    style={{
                      width: "56px",
                      height: "56px",
                      background:
                        "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                      boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                    }}
                  >
                    <IconComponent
                      style={{ fontSize: "1.4rem", color: "white" }}
                    />
                  </div>
                  <h3
                    className="font-bold mb-2 text-center"
                    style={{
                      color: "#2E2E2E",
                      fontWeight: "800",
                      fontSize: "1rem",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-center"
                    style={{
                      color: "#64748B",
                      fontSize: "0.85rem",
                      lineHeight: "1.5",
                    }}
                  >
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-2xl md:text-3xl font-bold mb-8 text-center"
            style={{
              color: "#EE2E24",
              letterSpacing: "-0.8px",
              fontWeight: "800",
            }}
          >
            Why Choose Trivana for Corporate Travel?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-lg"
                style={{
                  boxShadow: "0 3px 12px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center rounded-full"
                  style={{
                    width: "32px",
                    height: "32px",
                    background: "#22C55E",
                  }}
                >
                  <FaCheckCircle style={{ color: "white", fontSize: "1rem" }} />
                </div>
                <p
                  style={{
                    color: "#2E2E2E",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    margin: 0,
                  }}
                >
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section
        className="py-12"
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div
              className="bg-white rounded-lg p-8 text-center"
              style={{
                boxShadow: "0 4px 20px rgba(238, 46, 36, 0.1)",
                border: "1px solid rgba(238, 46, 36, 0.1)",
              }}
            >
              <div
                className="inline-flex items-center justify-center rounded-full mb-4"
                style={{
                  width: "70px",
                  height: "70px",
                  background:
                    "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                  boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                }}
              >
                <FaBell style={{ fontSize: "1.8rem", color: "white" }} />
              </div>
              <h2
                className="text-2xl md:text-3xl font-bold mb-3"
                style={{
                  color: "#EE2E24",
                  letterSpacing: "-0.8px",
                  fontWeight: "800",
                }}
              >
                Join the Waitlist
              </h2>
              <p
                className="mb-6"
                style={{
                  color: "#64748B",
                  fontSize: "0.95rem",
                  lineHeight: "1.6",
                }}
              >
                Be the first to know when our Corporate Bookings portal goes
                live. Get exclusive early access and special launch offers!
              </p>

              <form
                onSubmit={handleWaitlistSubmit}
                className="flex flex-col md:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your business email"
                  required
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: "2px solid rgba(238, 46, 36, 0.2)",
                    fontSize: "0.95rem",
                    fontFamily: "'Inter', sans-serif",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                  }
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 rounded-lg font-bold transition-all duration-300"
                  style={{
                    background: submitting ? "#ccc" : "#EE2E24",
                    color: "white",
                    fontSize: "0.95rem",
                    fontWeight: "700",
                    letterSpacing: "-0.3px",
                    boxShadow: "0 4px 12px rgba(238, 46, 36, 0.3)",
                    border: "none",
                    cursor: submitting ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.background = "#D82820";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 6px 16px rgba(238, 46, 36, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.background = "#EE2E24";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 4px 12px rgba(238, 46, 36, 0.3)";
                    }
                  }}
                >
                  {submitting ? "Joining..." : "Notify Me"}
                </button>
              </form>

              <div
                className="mt-6 pt-6"
                style={{ borderTop: "1px solid rgba(238, 46, 36, 0.1)" }}
              >
                <p
                  style={{
                    color: "#64748B",
                    fontSize: "0.875rem",
                    marginBottom: "8px",
                  }}
                >
                  Need corporate solutions right now?
                </p>
                <a
                  href="mailto:booktrivana@gmail.com"
                  className="inline-flex items-center gap-2"
                  style={{
                    color: "#EE2E24",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                  }}
                >
                  <FaEnvelope />
                  booktrivana@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="text-white py-12"
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-3"
            style={{
              letterSpacing: "-0.8px",
              fontWeight: "800",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Ready to Transform Your Corporate Travel?
          </h2>
          <p
            className="mb-6 text-base max-w-2xl mx-auto"
            style={{
              opacity: 0.95,
              fontWeight: "500",
            }}
          >
            Join leading companies that trust Trivana Hospitality for their
            business travel needs
          </p>
          <div className="flex flex-wrap gap-4 justify-center items-center">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <FaCheckCircle />
              <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
                100+ Corporate Clients
              </span>
            </div>
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <FaCheckCircle />
              <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
                2k+ Bookings
              </span>
            </div>
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <FaCheckCircle />
              <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
                30% Cost Savings
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateBookings;
